import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import ListViewSettings from "../../components/ListViewSettings";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { getAllCheckIn, printDcCheckin } from "../../services/CheckInServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildCheckInQueryParams } from "../../utils/listViewDateRanges";

function mapCheckInRow(checkin) {
    return {
        id: checkin.id,
        checkout_no: checkin.checkout_no,
        delivery_challan_no: checkin.delivery_challan?.delivery_challan_no,
        delivery_challan_date: checkin.delivery_challan?.delivery_challan_date,
        order_no: checkin.delivery_challan?.work_schedule?.sales_order_detail?.sales_order?.order_no,
        customer_name: checkin.delivery_challan?.work_schedule?.sales_order_detail?.sales_order?.consignee?.customer?.name,
        consignee_name: checkin.delivery_challan?.work_schedule?.sales_order_detail?.sales_order?.consignee?.name,
        grade: checkin.delivery_challan?.work_schedule?.sales_order_detail?.product?.name,
        dc_qty: checkin.delivery_challan?.dc_qty,
        accepted_qty: checkin.accepted_qty,
        customer_status: checkin.customer_status ? checkin.customer_status : "Waiting for checkin",
        cus_dc_status: checkin.cus_dc_status ? checkin.cus_dc_status : "Waiting for checkin",
        vehicle_no: checkin.delivery_challan?.vehicle?.name,
        driver_name: checkin.driver_name,
        hide_row_edit: checkin.customer_status ? "No" : "Yes",
        plant: checkin.delivery_challan?.work_schedule?.plant?.alias_name,
    };
}

function CheckInTable() {
    const InitialData = {
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);

    const columns = useMemo(
        () => [
            { fieldName: "plant", headerName: 'Plant' },
            { fieldName: "checkout_no", headerName: 'CheckOut No.' },
            { fieldName: "delivery_challan_no", headerName: 'DC Nos' },
            { fieldName: "delivery_challan_date", headerName: 'DC Date' },
            { fieldName: "order_no", headerName: 'SO Nos' },
            { fieldName: 'customer_name', headerName: 'Billing Name' },
            { fieldName: "consignee_name", headerName: 'Site Name' },
            { fieldName: "grade", headerName: 'Grade' },
            { fieldName: "dc_qty", headerName: 'DC Qty' },
            { fieldName: "accepted_qty", headerName: 'Accepted Qty' },
            { fieldName: "customer_status", headerName: 'Customer Status' },
            { fieldName: "cus_dc_status", headerName: 'DC Status' },
            { fieldName: "vehicle_no", headerName: 'Vehicle No' },
            { fieldName: 'driver_name', headerName: 'Drivers Name' },
            { fieldName: "id", headerName: "ROW_ACTIONS" },
        ],
        [],
    );

    const loadCheckInList = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllCheckIn(cookies, buildCheckInQueryParams(config))
            .then(checkinList => {
                const tableData = (checkinList?.checkin_list || []).map(mapCheckInRow);
                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Failed to load check in list');
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadCheckInList(config);
    }, [loadCheckInList]);

    useEffect(() => {
        getListViewSettings(cookies, 'check_in')
            .then((response) => {
                loadCheckInList(response.config || {});
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Failed to load list settings');
            });
    }, [cookies, loadCheckInList, refreshKey]);

    const onEditCheckIn = (checkinId) => {
        navigate(`/CheckInEdit/${checkinId}/`);
    };

    const onPrintDeliveryChallan = (checkinId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printDcCheckin(cookies, checkinId)
            .then(response => {
                openPdfResponse(response, {
                    title: 'Check In DC',
                    downloadFileName: 'checkin_dc.pdf',
                });
                setIsLoading(false);
            })
            .catch(error => {
                displayError(error, 'Print Failed');
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(c => c.headerName),
            data.records.map(checkin => ({
                plant: checkin.plant,
                checkout_no: checkin.checkout_no,
                delivery_challan_no: checkin.delivery_challan_no,
                delivery_challan_date: checkin.delivery_challan_date,
                order_no: checkin.order_no,
                customer_name: checkin.customer_name,
                consignee_name: checkin.consignee_name,
                grade: checkin.grade,
                dc_qty: checkin.dc_qty,
                accepted_qty: checkin.accepted_qty,
                customer_status: checkin.customer_status,
                cus_dc_status: checkin.cus_dc_status,
                vehicle_no: checkin.vehicle_no,
                driver_name: checkin.driver_name,
            }))
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of CheckIn',
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(col => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map(checkin => ({
                plant: checkin.plant,
                checkout_no: checkin.checkout_no,
                delivery_challan_no: checkin.delivery_challan_no,
                delivery_challan_date: checkin.delivery_challan_date,
                order_no: checkin.order_no,
                customer_name: checkin.customer_name,
                consignee_name: checkin.consignee_name,
                grade: checkin.grade,
                dc_qty: checkin.dc_qty,
                accepted_qty: checkin.accepted_qty,
                customer_status: checkin.customer_status,
                cus_dc_status: checkin.cus_dc_status,
                vehicle_no: checkin.vehicle_no,
                driver_name: checkin.driver_name,
            })),
            'CheckInList.pdf'
        );
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/CheckIn"
                    addNewLabel="New Check In"
                    homeLink="/Home"
                />
                <div className="container page-content-rail">
                    <PremiumPageHeader
                        module="GATE"
                        title="List of Check In"
                        subtitle="Configure filters using the settings icon. The table loads based on your saved date range preferences."
                        modeLabel="VIEW MODE"
                        headerActions={(
                            <ListViewSettings
                                viewKey="check_in"
                                onSaved={handleSettingsSaved}
                            />
                        )}
                    />
                    <div className="item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='List of CheckIn'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditCheckIn}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintDeliveryChallan}
                            hidePrint={false}
                        />
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
}

export default CheckInTable;
